import { motion } from "motion/react";
import { CheckCircle2 } from "lucide-react";

const STEP_COLORS: Record<string, string> = {
  SEE: "#4DA6D4",
  CHANGE: "#9B4FD4",
  ADAPT: "#5BBFB5",
  USE: "#C47B9F",
  LEARN: "#5C6BC0",
  EXIT: "#C4785A",
};

interface JourneyProgressProps {
  currentStep: number;
  totalSteps: number;
  steps: { title: string; completed: boolean }[];
  onStepClick?: (stepIndex: number) => void;
}

export function JourneyProgress({ currentStep, totalSteps, steps, onStepClick }: JourneyProgressProps) {
  return (
    <div className="fixed top-[calc(1.5rem+var(--env-banner-height,0px))] right-3 sm:right-6 z-50">
      <motion.div
        initial={{ x: 60, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ delay: 0.6, duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
        className="bg-[#063F6B]/95 backdrop-blur-md rounded-2xl p-3.5 sm:p-4 border border-white/[0.08] shadow-xl min-w-[160px] sm:min-w-[200px] max-w-[calc(100vw-5rem)]"
      >
        <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest mb-3">
          Progress
        </div>
        
        <div className="space-y-1 mb-4">
          {steps.map((step, index) => {
            const isClickable = onStepClick !== undefined;
            const isCurrent = index === currentStep;
            const isCompleted = step.completed;
            const stepColor = STEP_COLORS[step.title];
            
            return (
              <button
                key={index}
                onClick={() => onStepClick?.(index)}
                disabled={!isClickable}
                className={`w-full flex items-center gap-2.5 rounded-lg px-2 py-1.5 transition-all duration-200 ${
                  isClickable ? 'hover:bg-white/[0.05] cursor-pointer' : 'cursor-default'
                } ${isCurrent ? 'bg-white/[0.05]' : ''}`}
              >
                <div
                  className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center transition-colors ${
                    isCompleted 
                      ? 'bg-emerald-500/80' 
                      : !isCurrent 
                        ? 'bg-white/[0.06]'
                        : ''
                  }`}
                  style={isCurrent && !isCompleted ? { backgroundColor: stepColor || '#EAA059' } : undefined}
                >
                  {isCompleted ? (
                    <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                  ) : (
                    <span className="text-[10px] font-semibold text-white/80">{index + 1}</span>
                  )}
                </div>
                <span
                  className={`text-xs transition-colors ${
                    isCompleted 
                      ? 'text-emerald-400/80' 
                      : isCurrent 
                        ? 'font-semibold' 
                        : 'text-slate-500'
                  }`}
                  style={isCurrent && !isCompleted ? { color: stepColor || '#EAA059' } : undefined}
                >
                  {step.title}
                </span>
              </button>
            );
          })}
        </div>

        <div className="h-1 bg-white/[0.06] rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-[#EAA059] to-[#d4893e] rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>
        
        <div className="text-[10px] text-slate-500 mt-2 text-center">
          {Math.round(((currentStep + 1) / totalSteps) * 100)}%
        </div>
      </motion.div>
    </div>
  );
}
